import { Alert, Button, Grid, Select, Space, Table, Tag, Typography } from "antd";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import type { components } from "@qorakul/shared/api";
import { api } from "../app/api.ts";

dayjs.extend(utc);
dayjs.extend(timezone);

type SecurityEvent = components["schemas"]["SecurityEventDetails"];
type SecurityEventType = components["schemas"]["SecurityEventType"];

/** The school's time zone; events are stored in UTC and shown in Tashkent time. */
const SCHOOL_TIME_ZONE = "Asia/Tashkent";
const USER_PAGE_SIZE = 10;
const LOG_PAGE_SIZE = 25;
const DATE_TIME_FORMAT = "DD.MM.YYYY HH:mm";

const typeColor: Record<SecurityEventType, string> = {
  LoginSucceeded: "green",
  LoginFailed: "orange",
  LockedOut: "red",
  Logout: "default",
  PasswordResetRequested: "blue",
  PasswordResetEmailIssued: "blue",
  PasswordResetCompleted: "geekblue",
  PermissionDenied: "volcano",
  RefreshTokenReused: "magenta",
  TelegramSignInSucceeded: "cyan",
  TelegramSignInFailed: "orange",
};

const eventTypes = Object.keys(typeColor) as SecurityEventType[];

interface Props {
  /** Shows only this user's events; without it the whole log is listed with a user column and an event-type filter. */
  userId?: string;
}

/** Sign-in, session, password reset and permission-denial events from GET /audit/security-events. Render it only for users holding audit.read. */
export function SecurityEvents({ userId }: Props) {
  const { t } = useTranslation();
  const screens = Grid.useBreakpoint();
  const [page, setPage] = useState(1);
  const [eventType, setEventType] = useState<SecurityEventType | undefined>(undefined);
  const isLog = userId === undefined;
  const pageSize = isLog ? LOG_PAGE_SIZE : USER_PAGE_SIZE;

  const query = useQuery({
    queryKey: ["security-events", { userId, eventType, page }],
    queryFn: async () => {
      const { data, error } = await api.GET("/audit/security-events", {
        params: {
          query: {
            Page: page,
            PageSize: pageSize,
            ...(userId === undefined ? {} : { UserId: userId }),
            ...(eventType === undefined ? {} : { EventType: eventType }),
          },
        },
      });
      if (error || !data) throw new Error(t("app.error"));
      return data;
    },
    placeholderData: keepPreviousData,
  });

  function detailText(type: SecurityEventType, detail: string): string {
    if (type === "PermissionDenied") return t("audit.security.permissionDetail", { code: detail });
    if (type === "RefreshTokenReused") return t("audit.security.revokedSessionsDetail", { count: Number(detail) });
    // The Mini App roles granted, e.g. "Teacher,Parent".
    if (type === "TelegramSignInSucceeded") return detail.split(",").map((role) => t(`roles.${role}`, { defaultValue: role })).join(", ");
    return t(`audit.security.details.${detail}`, { defaultValue: detail });
  }

  const missing = <Typography.Text type="secondary">—</Typography.Text>;
  const columns = [
    {
      title: t("audit.security.columns.time"),
      key: "time",
      width: 140,
      render: (_: unknown, event: SecurityEvent) => dayjs(event.occurredAt).tz(SCHOOL_TIME_ZONE).format(DATE_TIME_FORMAT),
    },
    ...(isLog
      ? [
          {
            title: t("audit.security.columns.user"),
            key: "user",
            width: 200,
            render: (_: unknown, event: SecurityEvent) => (event.userName ? <Typography.Text>{event.userName}</Typography.Text> : missing),
          },
        ]
      : []),
    {
      title: t("audit.security.columns.event"),
      key: "event",
      width: 210,
      render: (_: unknown, event: SecurityEvent) => <Tag color={typeColor[event.eventType]}>{t(`audit.security.types.${event.eventType}`)}</Tag>,
    },
    {
      title: t("audit.security.columns.detail"),
      key: "detail",
      render: (_: unknown, event: SecurityEvent) => (event.detail ? detailText(event.eventType, event.detail) : missing),
    },
    {
      title: t("audit.security.columns.clientIp"),
      key: "clientIp",
      width: 150,
      render: (_: unknown, event: SecurityEvent) => (event.clientIp ? <Typography.Text code>{event.clientIp}</Typography.Text> : missing),
    },
  ];

  return (
    <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
      {isLog && (
        <Select<SecurityEventType | undefined>
          allowClear
          value={eventType}
          placeholder={t("audit.security.allTypes")}
          aria-label={t("audit.security.typeFilter")}
          style={{ width: screens.md ? 280 : "100%" }}
          options={eventTypes.map((type) => ({ value: type, label: t(`audit.security.types.${type}`) }))}
          onChange={(value) => {
            setEventType(value);
            setPage(1);
          }}
        />
      )}
      {query.isError && <Alert type="error" showIcon title={t("app.error")} action={<Button onClick={() => void query.refetch()}>{t("app.retry")}</Button>} />}
      <Table<SecurityEvent>
        rowKey="id"
        size={screens.md ? "middle" : "small"}
        loading={query.isPending || (query.isFetching && query.isPlaceholderData)}
        columns={columns}
        dataSource={query.data?.items ?? []}
        locale={{ emptyText: t(isLog ? "audit.security.logEmpty" : "audit.security.empty") }}
        scroll={{ x: isLog ? 800 : 600 }}
        pagination={{
          current: page,
          pageSize,
          total: Number(query.data?.totalCount ?? 0),
          showSizeChanger: false,
          hideOnSinglePage: true,
          ...(isLog ? { showTotal: (total: number) => t("audit.security.total", { count: total }) } : {}),
          onChange: setPage,
        }}
      />
    </Space>
  );
}
